import { graphql, PageProps } from 'gatsby';
import React, { ReactElement } from 'react';
import { ButtonContentfulModel } from 'src/api/models/Button';
import { FeatureListContentfulModel } from 'src/api/models/FeatureList';
import { HeadingContentfulModel } from 'src/api/models/Heading';
import { ImageContentfulModel } from 'src/api/models/Image';
import { PhoneCarouselContentfulModel } from 'src/api/models/PhoneCarousel';
import { ShortParagraphContentModel } from 'src/api/models/ShortParagraph';
import BannerEngagementDrop from 'src/organisms/banners/banner.engagementDrop.landing';
import PhoneCarouselEngagementDrop from 'src/organisms/carousels/PhoneCarousel.engagementDrop';
import AreYouReadyBanner from 'src/organisms/engagementDrop/engagementDrop.areYouReady';
import CreatorTestemony from 'src/organisms/engagementDrop/engagementDrop.creatorTestimony';
import FeatureListEngagement from 'src/organisms/featureList/featureList.engagementDrop';
import Layout from '../elements/layout/layout';

interface EngagementSectionContentfulModel {
  label:
    | 'Engagement Drop Banner Container'
    | 'Feature Items Engagement Container'
    | 'Get Ready Section Container'
    | 'Phone Carousel Engagement Container'
    | 'Game Changer Section Container'
    | 'Are You Ready Section Container';
  internal: {
    type: 'ContentfulSection';
  };
  contentfulchildren: Array<
    | HeadingContentfulModel
    | ImageContentfulModel
    | FeatureListContentfulModel
    | ButtonContentfulModel
    | ShortParagraphContentModel
    | PhoneCarouselContentfulModel
  >;
}

interface EngagementPageProps extends PageProps {
  data: {
    contentfulPage: {
      title: string;
      seoTitle: string;
      seoDescription?: string;
      displayPromotionModal?: boolean;
      seoImage?: {
        file?: {
          url: string;
        };
      };
      contentfulchildren: EngagementSectionContentfulModel[];
    };
  };
}

const EngagementPage = (props: EngagementPageProps): ReactElement => {
  const pageData = props.data.contentfulPage;
  const helmetData = {
    title: pageData.seoTitle,
    description: pageData.seoDescription,
    previewImageURL: pageData.seoImage?.file?.url
  }

  // Banner Section
  const bannerSectionData = pageData.contentfulchildren.find(data => data.label.includes('Engagement Drop Banner Container')).contentfulchildren;
  const bannerHeaders = bannerSectionData.filter(data => data.internal?.type === 'ContentfulHeading') as HeadingContentfulModel[];
  const bannerBackgroundImage = bannerSectionData.find(data => data.internal?.type === 'ContentfulImage') as ImageContentfulModel;

  // Get Ready Section
  const getReadySection = pageData.contentfulchildren.find(data => data.label.includes('Get Ready Section Container')).contentfulchildren;
  const getReadyTitle = getReadySection.find(data => data.internal.type === 'ContentfulHeading') as HeadingContentfulModel;
  const getReadyDescription = (getReadySection.filter(data => data.internal.type === 'ContentfulShortParagraph') as ShortParagraphContentModel[]).map(paragraph => paragraph.text);
  const getReadyButton = getReadySection.find(data => data.internal.type === 'ContentfulButton') as ButtonContentfulModel;

  // Features Section
  const featureListSectionData = pageData.contentfulchildren.find(data => data.label.includes('Feature Items Engagement Container')).contentfulchildren;
  const features = featureListSectionData.find(data => data.internal.type === 'ContentfulFeatures') as FeatureListContentfulModel;

  // Phone Carousel Section
  const phoneCarouselSection = pageData.contentfulchildren.find(data => data.label.includes('Phone Carousel Engagement Container')).contentfulchildren;
  const phoneCarouselTitle = phoneCarouselSection.find(data => data.internal.type === 'ContentfulHeading') as HeadingContentfulModel;
  const phoneCarouselDescription = phoneCarouselSection.find(data => data.internal.type === 'ContentfulShortParagraph') as ShortParagraphContentModel;
  const phoneCarouselItems = phoneCarouselSection.find(data => data.internal.type === 'ContentfulPhoneCarousel') as PhoneCarouselContentfulModel;

  // Creator Testemony Section
  const creatorTestemonySection = pageData.contentfulchildren.find(data => data.label.includes('Game Changer Section Container')).contentfulchildren;
  const creatorTestemonyHeadings = (creatorTestemonySection.filter(data => data.internal.type === 'ContentfulHeading') as HeadingContentfulModel[]).map(heading => heading.text);
  const creatorsCardHeadings = creatorTestemonyHeadings.filter((_, index) => index !== 0);
  const creatorsTestemonyImage = creatorTestemonySection.find(data => data.internal.type === 'ContentfulImage') as ImageContentfulModel;
  const creatorsTestemonyDescription = creatorTestemonySection.find(data => data.internal.type === 'ContentfulShortParagraph') as ShortParagraphContentModel;

  // Are You Ready Section
  const areYouReadySection = pageData.contentfulchildren.find(data => data.label.includes('Are You Ready Section Container')).contentfulchildren;
  const areYouReadyTitle = areYouReadySection.find(data => data.internal.type === 'ContentfulHeading') as HeadingContentfulModel;
  const areYouReadyDescription = areYouReadySection.find(data => data.internal.type === 'ContentfulShortParagraph') as ShortParagraphContentModel;
  const areYouReadyButton = areYouReadySection.find(data => data.internal.type === 'ContentfulButton') as ButtonContentfulModel;

  return (
    <Layout
      currentPageId="5Hg7B1MF3g4huZcO8RejkB"
      helmet={helmetData}
      location={props.location}
      theme="engagementDrop"
    >
      <BannerEngagementDrop
        title={bannerHeaders[ 0 ].text}
        subtitle={bannerHeaders[ 1 ].text}
        backgroundImage={bannerBackgroundImage.image.fullWidthImageData}
        mobileBackgroundImage={bannerBackgroundImage.mobileImage.fullWidthImageData}
      />
      <FeatureListEngagement
        title={getReadyTitle.text}
        actionButton={getReadyButton}
        descriptionParagraphs={getReadyDescription}
        features={features.contentfulchildren}
      />
      <PhoneCarouselEngagementDrop
        title={phoneCarouselTitle.text}
        description={phoneCarouselDescription.text}
        phoneItems={phoneCarouselItems.contentfulchildren}
      />
      <CreatorTestemony
        title={creatorTestemonyHeadings[ 0 ]}
        creatorImage={creatorsTestemonyImage.image.fullWidthImageData}
        card={{
          description: creatorsTestemonyDescription.text,
          creatorIdentification: creatorsCardHeadings
        }}
      />
      <AreYouReadyBanner
        title={areYouReadyTitle.text}
        description={areYouReadyDescription.text}
        actionButton={areYouReadyButton}
      />
    </Layout>
  );
};

export const pageQuery = graphql`
   query EngagementPageQuery {
     contentfulPage(contentful_id: {eq: "5Hg7B1MF3g4huZcO8RejkB"}) {
       title
       seoTitle
       seoDescription
       seoImage {
         file {
           url
         }
       }
       displayPromotionModal
       contentfulchildren {
         id
         label
         contentfulchildren {
           ...HeadingContent
           ...ImageContent
           ...ShortParagraphContent
           ...ButtonContent
           ...FeaturesContent
           ...PhoneCarouselContent
         }
       }
     }
   }
`;

export default EngagementPage;
